import React from 'react';

const VIEW_STATE_PREFIX = 'wcf:view-state:';

// Mirrors React's own useState contract: a value, or a factory/updater that
// produces one. The two assertions below are the standard consequence of that
// shape — an unconstrained `T` may itself be a function type, so `typeof x ===
// 'function'` cannot on its own tell "the value" apart from "a producer of the
// value". React's own typings resolve it exactly this way.
export type ViewStateInitializer<T> = T | (() => T);
export type ViewStateUpdater<T> = (previous: T) => T;

function resolveInitialValue<T>(initialValue: ViewStateInitializer<T>): T {
  return typeof initialValue === 'function' ? (initialValue as () => T)() : (initialValue as T);
}

function isCompatibleValue(value: unknown, fallback: unknown): boolean {
  if (fallback == null) return true;
  if (Array.isArray(fallback)) return Array.isArray(value);
  if (typeof fallback === 'object') return value != null && typeof value === 'object' && !Array.isArray(value);
  return typeof value === typeof fallback;
}

function readStoredValue<T>(key: string, initialValue: ViewStateInitializer<T>): T {
  const fallback = resolveInitialValue(initialValue);
  if (typeof window === 'undefined') return fallback;
  // Reading window.sessionStorage itself throws (SecurityError) where site
  // storage is blocked, so the access sits inside the try with the rest.
  try {
    if (!window.sessionStorage) return fallback;
    const raw = window.sessionStorage.getItem(VIEW_STATE_PREFIX + key);
    if (raw == null) return fallback;
    // Parsed storage is genuinely unknown until isCompatibleValue() vets it
    // against the fallback's shape; that check IS the narrowing.
    const parsed: unknown = JSON.parse(raw);
    return isCompatibleValue(parsed, fallback) ? (parsed as T) : fallback;
  } catch {
    return fallback;
  }
}

function writeStoredValue(key: string, value: unknown): void {
  if (typeof window === 'undefined') return;
  try {
    if (!window.sessionStorage) return;
    window.sessionStorage.setItem(VIEW_STATE_PREFIX + key, JSON.stringify(value));
  } catch {
    // Best effort only; navigation state should never break rendering.
  }
}

export function usePersistentViewState<T>(
  key: string,
  initialValue: ViewStateInitializer<T>,
): [T, (nextValue: T | ViewStateUpdater<T>) => void] {
  const [value, setValue] = React.useState(() => readStoredValue(key, initialValue));
  const valueRef = React.useRef(value);
  const setPersistentValue = React.useCallback(
    (nextValue: T | ViewStateUpdater<T>) => {
      // The assertion is unavoidable and is the same one React's own typings
      // make: an unconstrained `T` may itself be a function type, so narrowing
      // `T | ViewStateUpdater<T>` by `typeof === 'function'` leaves the
      // uncallable `T & Function` in the union. `resolved` is pinned to `T` so
      // nothing downstream widens.
      const resolved: T =
        typeof nextValue === 'function' ? (nextValue as ViewStateUpdater<T>)(valueRef.current) : nextValue;
      valueRef.current = resolved;
      writeStoredValue(key, resolved);
      setValue(resolved);
    },
    [key],
  );

  React.useEffect(() => {
    valueRef.current = value;
    writeStoredValue(key, value);
  }, [key, value]);

  return [value, setPersistentValue];
}
